<template>
    <div class="body_expenses_analysis">
        <div v-if="isLoading" class="state_message">
            <p>Cargando análisis…</p>
        </div>

        <div v-else-if="error" class="state_message">
            <p class="error_text">{{ error }}</p>
        </div>

        <div v-else-if="analysis" class="analysis_grid">
            <AccountStatementCard class="cell"
                title="Gastos"
                :period-label="analysis.period_label"
                :headline="analysis.headline"
                :statement="analysis.statement"
                :selected-account="selectedAccount"
                :selected-level="selectedAccountLevel"
                @select-account="onSelectAccount" />

            <CounterpartsRankingCard class="cell"
                v-model="counterpartsQueryModel"
                :counterparts="filteredCounterparts"
                @counterpart-click="openCounterpartDetail" />
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import AccountStatementCard from "@/components/financialReports/AccountStatementCard.vue";
// El ranking de contrapartes es presentacional y category-agnostic — se reusa
// tal cual del feature de Costos (mismas pills de tipo, barra, monto·share).
import CounterpartsRankingCard from "@/components/financialReports/costsAnalysis/CounterpartsRankingCard.vue";
import ModalFullHeightRight from "@/components/modals/ModalFullHeightRight.vue";
import HeaderClientDetail from "@/components/financialReports/salesAnalysis/clientDetail/HeaderClientDetail.vue";
import BodyClientDetail from "@/components/financialReports/salesAnalysis/clientDetail/BodyClientDetail.vue";
import { obligationForType } from "@/utils/counterpartObligation";

export default {
    name: "BodyExpensesAnalysis",
    components: {
        AccountStatementCard,
        CounterpartsRankingCard,
    },
    // Orquestador del feature: lee del módulo expensesAnalysis y reparte slices
    // a las dos cards presentacionales. El modal pasa `bodyProps: null`.
    computed: {
        ...mapState("expensesAnalysis", {
            analysis: "data",
            isLoading: "isLoading",
            error: "error",
            analysisMonth: "month",
            selectedAccount: "account",
            selectedAccountLevel: "accountLevel",
        }),
        ...mapGetters("expensesAnalysis", ["filteredCounterparts"]),
        counterpartsQueryModel: {
            get() { return this.$store.state.expensesAnalysis.counterpartsQuery; },
            set(value) { this.setCounterpartsQuery(value); },
        },
    },
    methods: {
        ...mapMutations("expensesAnalysis", { setCounterpartsQuery: "SET_COUNTERPARTS_QUERY" }),
        ...mapActions("expensesAnalysis", ["selectRankingAccount"]),
        ...mapActions("clientDetail", {
            openClientDetailAction: "openClientDetail",
            closeClientDetailAction: "closeClientDetail",
            changeClientRangePeriod: "changeRangePeriod",
        }),

        obligationForType,

        // La card del estado (card 1) eligió una cuenta: recorta el ranking a
        // esa cuenta sin recargar el modal. { account: null } = todas (Gastos),
        // { account, level: "2" } = grupo L2, { account, level: "3" } = cuenta L3.
        onSelectAccount({ account, level }) {
            this.selectRankingAccount({ account, level });
        },

        // Drill-down de segundo nivel: reutiliza el drawer clientDetail (mismo
        // que Ventas/Costos) parametrizado para Gastos. document_type='P' (lado
        // egreso, igual que Costos); level1Name='Gastos' acota docs+serie a
        // gastos; el denominador "% de Ventas" del chart sigue en level2Name.
        openCounterpartDetail(counterpart) {
            if (!counterpart || !counterpart.name || !this.analysisMonth) return;

            this.openClientDetailAction({
                alias: counterpart.name,
                documentType: "P",
                monthIso: this.analysisMonth,
                obligationName: this.obligationForType(counterpart.counterpart_type),
                level1Name: "Gastos",
                counterpartType: counterpart.counterpart_type,
            });

            this.$buefy.modal.open({
                parent: this,
                hasModalCard: true,
                customClass: "wide-summary-modal",
                canCancel: ["escape", "outside"],
                component: ModalFullHeightRight,
                props: {
                    headerComp: HeaderClientDetail,
                    bodyComp: BodyClientDetail,
                    headerProps: { fallbackAlias: counterpart.name },
                    bodyProps: null,
                    // Theme del tab Gastos al segundo drawer + width menor (70%)
                    // para que se perciba apilado sobre el análisis del mes.
                    theme: "gastos",
                    width: "70%",
                },
                events: {
                    closed: this.closeClientDetailAction,
                    "update-range-period": this.changeClientRangePeriod,
                },
            });
        },
    },
};
</script>

<style scoped lang="scss">
// Misma cadena de altura que BodyCostsAnalysis: el body absorbe el flex:1 de
// .modal_back_caa y lo propaga; el scroll vive dentro de cada card.
.body_expenses_analysis {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.state_message {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    text-align: center;
    color: var(--report-text-muted);
    font-size: 14px;
}

.error_text {
    color: var(--report-negative);
}

// 2 columnas: estado de gastos (izquierda) + ranking de contrapartes (derecha).
.analysis_grid {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
}

.cell {
    min-width: 0;
    min-height: 0;
}
</style>
